import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Button from "@/components/ui/button";
import ConfirmModal from "@/components/ui/confirm-modal";
import { ExamQuestionPreview } from "@/features/exam-management";
import type { ExamPreviewQuestion } from "@/features/exam-management";
import { clientExamsApi } from "../services/client-exams.api";
import {
  resolveFollowUpAction,
  type EducationFollowUpAction,
} from "@/features/client-educations/utils/follow-up-url";
import { ExamAttemptStatus } from "../types/client-exams.types";
import type {
  ClientExamStartAttemptDto,
  ClientExamQuestionsQuestionDto,
} from "../types/client-exams.types";
import styles from "./TakeExamPage.module.scss";

function pad2(n: number): string {
  return String(Math.max(0, Math.floor(n))).padStart(2, "0");
}

function secondsToTimerDisplay(totalSeconds: number): {
  hours: string;
  minutes: string;
  seconds: string;
} {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return {
    hours: pad2(h),
    minutes: pad2(m),
    seconds: pad2(s),
  };
}

/** GET /exam/questions cevabındaki soruları ortak ön izleme tipine çevirir */
function mapQuestionsApiToPreview(
  questions: ClientExamQuestionsQuestionDto[]
): ExamPreviewQuestion[] {
  return [...questions]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((q) => ({
      id: q.id,
      text: q.questionText ?? "",
      options: (q.options ?? []).map((o) => ({
        id: o.id,
        text: o.optionText ?? "",
        isCorrect: undefined,
      })),
      allowMultiple: q.allowMultipleSelection ?? false,
      imagePreview:
        q.imageUrl ?? q.image?.downloadUrl ?? undefined,
    }));
}

/** GET /exam/questions cevabındaki selectedOptionIds'den answers map üretir */
function buildInitialAnswers(
  questions: ClientExamQuestionsQuestionDto[]
): Record<number, string[]> {
  const sorted = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const out: Record<number, string[]> = {};
  sorted.forEach((q, index) => {
    const ids = q.selectedOptionIds ?? [];
    if (ids.length) out[index] = ids;
  });
  return out;
}

/** API'den gelen sorularda selectedOptionIds dolu olanlar zaten gönderilmiş sayılır */
function buildInitialSubmitted(
  questions: ClientExamQuestionsQuestionDto[]
): Record<number, boolean> {
  const sorted = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const out: Record<number, boolean> = {};
  sorted.forEach((q, index) => {
    if ((q.selectedOptionIds?.length ?? 0) > 0) out[index] = true;
  });
  return out;
}

type TakeExamPhase = "loading" | "start" | "exam" | "summary";

const TakeExamPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { educationId } = useParams<{ educationId: string }>();

  const locationState = location.state as { examName?: string; examAttemptStatuses?: number[] } | null;
  const examNameFromState = locationState?.examName;
  const examAttemptStatusesFromState = locationState?.examAttemptStatuses;
  const lastAttemptStatus = examAttemptStatusesFromState?.length
    ? examAttemptStatusesFromState[examAttemptStatusesFromState.length - 1]
    : undefined;
  const skipStartAndGoToQuestions = lastAttemptStatus === ExamAttemptStatus.NOT_PARTICIPATED;

  const [examName, setExamName] = useState(examNameFromState ?? t("clientExams.takeExam.title"));
  const [phase, setPhase] = useState<TakeExamPhase>(skipStartAndGoToQuestions ? "loading" : "start");
  const [examQuestions, setExamQuestions] = useState<ExamPreviewQuestion[] | null>(null);
  const [attempt, setAttempt] = useState<ClientExamStartAttemptDto | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string[]>>({});
  const [submittedByQuestionIndex, setSubmittedByQuestionIndex] = useState<Record<number, boolean>>({});
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(0);
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  // Sync ref ile çift submission'ı engelliyoruz: React state batching/render
  // gecikmesinde butona ardışık iki tıklama veya etrenek çift tetikleme olabilir.
  const isFinishingRef = useRef(false);
  const [finishError, setFinishError] = useState<string | null>(null);
  const [followUp, setFollowUp] = useState<EducationFollowUpAction | null>(null);
  const [canGoPreviousQuestions, setCanGoPreviousQuestions] = useState(true);

  useEffect(() => {
    if (!educationId) {
      setError(t("clientExams.takeExam.invalidId"));
    }
  }, [educationId, t]);

  useEffect(() => {
    if (examNameFromState) setExamName(examNameFromState);
  }, [examNameFromState]);

  useEffect(() => {
    if (!educationId || !skipStartAndGoToQuestions) return;
    setPhase("loading");
    clientExamsApi
      .getExamQuestions(educationId)
      .then((item) => {
        setAttempt(item.attempt);
        setCanGoPreviousQuestions(
          (item as { canGoPreviousQuestions?: boolean })
            .canGoPreviousQuestions ??
            item.attempt.canGoPreviousQuestions ??
            true,
        );
        if (item.attempt.endsAt) {
          const end = new Date(item.attempt.endsAt).getTime();
          const now = Date.now();
          const remaining = Math.max(0, Math.floor((end - now) / 1000));
          setTimeRemainingSeconds(remaining);
        } else {
          setTimeRemainingSeconds(30 * 60);
        }
        setExamQuestions(mapQuestionsApiToPreview(item.questions));
        setAnswers(buildInitialAnswers(item.questions));
        setSubmittedByQuestionIndex(buildInitialSubmitted(item.questions));
        setPhase("exam");
      })
      .catch((err: unknown) => {
        const message =
          typeof err === "object" && err !== null && "response" in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        setError(
          message ||
            (err instanceof Error ? err.message : t("clientExams.takeExam.questionsLoadError"))
        );
        setPhase("start");
      });
  }, [educationId, skipStartAndGoToQuestions, t]);

  const handleStartExam = useCallback(() => {
    if (!educationId) return;
    setStartError(null);
    setStarting(true);
    clientExamsApi
      .startExam(educationId)
      .then((attemptData) => {
        setAttempt(attemptData);
        setCanGoPreviousQuestions(
          attemptData.canGoPreviousQuestions ?? true,
        );
        if (attemptData.endsAt) {
          const end = new Date(attemptData.endsAt).getTime();
          const now = Date.now();
          const remaining = Math.max(0, Math.floor((end - now) / 1000));
          setTimeRemainingSeconds(remaining);
        }
        return clientExamsApi.getExamQuestions(educationId);
      })
      .then((item) => {
        setAttempt(item.attempt);
        setCanGoPreviousQuestions(
          (item as { canGoPreviousQuestions?: boolean })
            .canGoPreviousQuestions ??
            item.attempt.canGoPreviousQuestions ??
            true,
        );
        if (item.attempt.endsAt) {
          const end = new Date(item.attempt.endsAt).getTime();
          const now = Date.now();
          const remaining = Math.max(0, Math.floor((end - now) / 1000));
          setTimeRemainingSeconds(remaining);
        }
        setExamQuestions(mapQuestionsApiToPreview(item.questions));
        setAnswers(buildInitialAnswers(item.questions));
        setSubmittedByQuestionIndex(buildInitialSubmitted(item.questions));
        setPhase("exam");
      })
      .catch((err: unknown) => {
        const message =
          typeof err === "object" && err !== null && "response" in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        setStartError(
          message ||
            (err instanceof Error ? err.message : t("clientExams.takeExam.startError"))
        );
      })
      .finally(() => setStarting(false));
  }, [educationId, t]);

  useEffect(() => {
    if (phase !== "exam" || !examQuestions?.length || timeRemainingSeconds <= 0) return;
    const interval = setInterval(() => {
      setTimeRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, examQuestions, timeRemainingSeconds]);

  const handleOptionChange = useCallback((questionIndex: number, optionIds: string[]) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: optionIds }));
  }, []);

  const handleSubmitAnswer = useCallback(
    async (questionId: string, selectedOptionIds: string[]) => {
      if (!educationId || !examQuestions?.length) return;
      setIsSubmittingAnswer(true);
      try {
        await clientExamsApi.submitExamAnswer(educationId, questionId, selectedOptionIds);
        const index = examQuestions.findIndex((q) => q.id === questionId);
        if (index >= 0) {
          setSubmittedByQuestionIndex((prev) => ({ ...prev, [index]: true }));
        }
        const isLastQuestion = index >= 0 && index === examQuestions.length - 1;
        if (isLastQuestion) {
          setPhase("summary");
        } else {
          setSelectedQuestionIndex((i) =>
            Math.min((examQuestions?.length ?? 1) - 1, i + 1)
          );
        }
      } finally {
        setIsSubmittingAnswer(false);
      }
    },
    [educationId, examQuestions]
  );

  const handleFinishExam = useCallback(() => {
    if (!educationId) return;
    if (isFinishingRef.current) return;
    isFinishingRef.current = true;
    setFinishError(null);
    setIsFinishing(true);
    clientExamsApi
      .finishExam(educationId)
      .then((result) => {
        if (result.followUpAction) {
          setFollowUp(result.followUpAction);
          return;
        }
        // followUpAction null: BE bir sonraki adımı bitmiş sayıyor. Geçtiyse sertifika,
        // geçemediyse (veya statü belirsizse) sınavlar listesine dön.
        if (result.status === "PASSED") {
          toast.success(
            t("clientExams.takeExam.passedCertificatePreparing"),
          );
          navigate("/dashboard/client-certificates");
        } else {
          navigate("/dashboard/client-exams");
        }
      })
      .catch((err: unknown) => {
        const errResp =
          typeof err === "object" && err !== null && "response" in err
            ? (err as {
                response?: { status?: number; data?: { message?: string } };
              }).response
            : undefined;
        const message = errResp?.data?.message ?? null;
        // Yanlış cevaplama durumunda backend attempt'i otomatik FAILED'a
        // çekmiş olabiliyor; UI sınav özetinde "Bitir" butonuna basınca
        // "Sınav başlatılmamış" hatası geliyor. Bu durumu graceful ele alıp
        // doğrudan sınavlar listesine yönlendiriyoruz; toast göstermiyoruz.
        const alreadyFinished =
          errResp?.status === 400 &&
          /sınav.*başlat/i.test(message ?? "");
        if (alreadyFinished) {
          navigate("/dashboard/client-exams");
          return;
        }
        setFinishError(
          message ||
            (err instanceof Error ? err.message : t("clientExams.takeExam.finishError"))
        );
      })
      .finally(() => {
        isFinishingRef.current = false;
        setIsFinishing(false);
      });
  }, [educationId, navigate, t]);

  const handleFollowUpConfirm = useCallback(() => {
    const followUpSnapshot = followUp;
    setFollowUp(null);
    const resolved = resolveFollowUpAction(followUpSnapshot?.actionUrl);
    if (!resolved) {
      navigate("/dashboard/client-exams");
      return;
    }
    // Aynı pathname'e navigate olunca React Router remount yapmaz; sınav tekrarı
    // senaryosunda (FAILED → /take/:id'ye yönlendiriliyoruz, zaten oradayız) bu
    // popup'ı kapatır ama özet ekranı state'i kalır. Bu yüzden state'i sıfırlayıp
    // yeni attempt'i doğrudan başlatıyoruz.
    if (resolved.path === location.pathname) {
      setPhase("start");
      setExamQuestions(null);
      setAnswers({});
      setSubmittedByQuestionIndex({});
      setSelectedQuestionIndex(0);
      setAttempt(null);
      setFinishError(null);
      setError(null);
      handleStartExam();
      return;
    }
    // Anket rotasında, backend'in popup başlığını modal title olarak kullanabilmek için
    // pollTitle'ı da state'e koy (ClientPollsPage bunu okuyor).
    const mergedState =
      resolved.path === "/dashboard/client-polls"
        ? { ...(resolved.state ?? {}), pollTitle: followUpSnapshot?.title }
        : resolved.state;
    navigate(resolved.path, mergedState ? { state: mergedState } : undefined);
  }, [followUp, navigate, location.pathname, handleStartExam]);

  const handleFollowUpClose = useCallback(() => {
    setFollowUp(null);
    navigate("/dashboard/client-exams");
  }, [navigate]);

  if (phase === "loading") {
    return (
      <DashboardLayout>
        <Header title={examName} />
        <div className={styles.loading}>
          {t("common.loading")}
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Header title={t("clientExams.takeExam.title")} />
        <div className={styles.error}>
          {error}
          <button
            type="button"
            className={styles.backLink}
            onClick={() => navigate("/dashboard/client-exams")}
          >
            {t("clientExams.takeExam.backToList")}
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (phase === "summary") {
    const submittedCount = examQuestions
      ? Object.keys(submittedByQuestionIndex).filter((i) => submittedByQuestionIndex[Number(i)]).length
      : 0;
    const totalCount = examQuestions?.length ?? 0;
    return (
      <DashboardLayout>
        <Header title={examName} />
        <div className={styles.startScreen}>
          <div className={styles.startCard}>
            <h2 className={styles.startTitle}>
              {t("clientExams.takeExam.summaryTitle")}
            </h2>
            <p className={styles.startExamName}>{examName}</p>
            <p className={styles.startMeta}>
              {t("clientExams.takeExam.summaryDescription")}
            </p>
            <p className={styles.startMeta}>
              {t("clientExams.takeExam.summaryCount", {
                answered: submittedCount,
                total: totalCount,
              })}
            </p>
            {finishError && (
              <p className={styles.startError} role="alert">
                {finishError}
              </p>
            )}
            <div className={styles.startActions}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={handleFinishExam}
                disabled={isFinishing}
              >
                {isFinishing
                  ? t("common.loading")
                  : t("clientExams.takeExam.finishButton")}
              </Button>
              <button
                type="button"
                className={styles.backLink}
                onClick={() => navigate("/dashboard/client-exams")}
              >
                {t("clientExams.takeExam.backToList")}
              </button>
            </div>
          </div>
        </div>
        <ConfirmModal
          isOpen={!!followUp}
          onClose={handleFollowUpClose}
          onConfirm={handleFollowUpConfirm}
          title={followUp?.title ?? ""}
          message={followUp?.message ?? ""}
          confirmText={followUp?.buttonLabel ?? t("common.continue")}
          cancelText={t("common.later")}
        />
      </DashboardLayout>
    );
  }

  if (phase === "start") {
    return (
      <DashboardLayout>
        <Header title={examName} />
        <div className={styles.startScreen}>
          <div className={styles.startCard}>
            <h2 className={styles.startTitle}>
              {t("clientExams.takeExam.startTitle")}
            </h2>
            <p className={styles.startExamName}>{examName}</p>
            <p className={styles.startMeta}>
              {t("clientExams.takeExam.startHint")}
            </p>
            {startError && (
              <p className={styles.startError} role="alert">
                {startError}
              </p>
            )}
            <div className={styles.startActions}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={handleStartExam}
                disabled={starting}
              >
                {starting
                  ? t("common.loading")
                  : t("clientExams.takeExam.startButton")}
              </Button>
              <button
                type="button"
                className={styles.backLink}
                onClick={() => navigate("/dashboard/client-exams")}
              >
                {t("clientExams.takeExam.backToList")}
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (phase !== "exam" || !examQuestions?.length) return null;

  const questions = examQuestions;
  const timerDisplay = secondsToTimerDisplay(timeRemainingSeconds);

  const handleSelectQuestion = (index: number) => {
    if (!questions?.length) return;
    if (!canGoPreviousQuestions) {
      // Geri gitmek yasak
      if (index < selectedQuestionIndex) return;
      // İleri sadece mevcut soru gönderildiyse serbest
      const isCurrentSubmitted =
        submittedByQuestionIndex[selectedQuestionIndex] === true;
      if (index > selectedQuestionIndex && !isCurrentSubmitted) return;
    }
    setSelectedQuestionIndex(index);
  };

  return (
    <DashboardLayout>
      <Header title={examName} />
      <div className={styles.container} data-attempt-id={attempt?.id ?? undefined}>
        <ExamQuestionPreview
          questions={questions}
          selectedQuestionIndex={selectedQuestionIndex}
          onSelectQuestion={handleSelectQuestion}
          onPrev={() => {
            if (!canGoPreviousQuestions) return;
            setSelectedQuestionIndex((i) => Math.max(0, i - 1));
          }}
          onNext={() => {
            if (!canGoPreviousQuestions) {
              const isCurrentSubmitted =
                submittedByQuestionIndex[selectedQuestionIndex] === true;
              if (!isCurrentSubmitted) return;
            }
            setSelectedQuestionIndex((i) =>
              Math.min(questions.length - 1, i + 1),
            );
          }}
          timerDisplay={timerDisplay}
          mode="take"
          selectedOptionIds={answers[selectedQuestionIndex] ?? []}
          onOptionChange={(optionIds) =>
            handleOptionChange(selectedQuestionIndex, optionIds)
          }
          answersByQuestionIndex={answers}
          submittedByQuestionIndex={submittedByQuestionIndex}
          onSubmitAnswer={handleSubmitAnswer}
          isSubmittingAnswer={isSubmittingAnswer}
        />
      </div>
    </DashboardLayout>
  );
};

export default TakeExamPage;
